import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  MEMBERSHIP_APPLICATION_DENIAL_EMAIL_PREVIEW_QUERY,
  type MembershipApplicationDenialEmailPreview,
  type MembershipApplicationDenialEmailPreviewQueryResponse,
  type MembershipApplicationDenialEmailPreviewQueryVariables
} from "../queries";

export function useMembershipApplicationDenialEmailPreview(applicationId: string) {
  const [preview, setPreview] = useState<MembershipApplicationDenialEmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    MembershipApplicationDenialEmailPreviewQueryResponse,
    MembershipApplicationDenialEmailPreviewQueryVariables
  >(MEMBERSHIP_APPLICATION_DENIAL_EMAIL_PREVIEW_QUERY, {
    fetchPolicy: "network-only"
  });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async (messageToApplicant?: string) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;
      const trimmedMessage = messageToApplicant?.trim();

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({
        variables: {
          applicationId,
          messageToApplicant: trimmedMessage || null
        }
      });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.membershipApplicationDenialEmailPreview ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [applicationId, fetchPreview]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
